/* eslint-disable camelcase */
/* eslint-disable prettier/prettier */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import {
  cargandoUsuarioLogueadoSelector,
  usuarioLogueadoSelector,
} from 'store/selectors';
import { history } from 'store/ConfigureStore';
import useWindowSize from 'hooks/useWindowSize';

import * as ROUTES from 'constants/RouterConstants';
// Iconos
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import PublicIcon from '@mui/icons-material/Public';
import EmailIcon from '@mui/icons-material/Email';
import iconoNivelIntermedio from 'assets/icons/basico_intermedio_sin_fondo.svg';

import BANDERA_BRASIL from 'assets/bandera-brasil.png';
import BANDERA_ARGENTINA from 'assets/bandera-argentina.png';
import BANDERA_PARAGUAY from 'assets/bandera-paraguay.png';
import BANDERA_CHILE from 'assets/bandera-chile.png';
import BANDERA_URUGUAY from 'assets/bandera-uruguay.png';

// Constantes
import {
  ANCHO_ESCRITORIO,
  INFO_ICONO_NIVEL_SEGURIDAD,
  CARD_SEGURIDAD_TITLE,
} from 'constants/commonConstants';

// Componentes
import Card from 'components/Card';
import CardSpinner from 'components/CardSpinner';
import TitleCard from 'components/TitleCard';
import ButtonSolid from 'components/ButtonSolid';

// Utils
import {
  esCiudadanoTemporal,
  correoElectronico,
  origenRegistro,
} from './utils';

// Estilos
import styles from './autogestionIdp.module.css';

const IDPS_EXTRANJEROS = [
  'Gov BR',
  'Autenticar',
  'Identificación Electrónica - Paraguay',
  'Clave Única - Chile',
];

const AutogestionIdp = () => {
  const { t, i18n } = useTranslation();

  const usuarioLogueado = useSelector(usuarioLogueadoSelector);
  const cargandoUsuarioLogueado = useSelector(cargandoUsuarioLogueadoSelector);

  const [esExtranjero, setEsExtranjero] = useState(false);
  const [redirectParaExtranjero, setRedirectParaExtranjero] = useState();
  const [nivelBajoSaml, setNivelBajoSaml] = useState();

  const [ancho] = useWindowSize();
  const esMovil = ancho < ANCHO_ESCRITORIO;

  const nivel = usuarioLogueado?.nivel;
  const nivelTexto = nivel?.substring(
    nivel.indexOf(' '),
    nivel.lastIndexOf('('),
  );

  useEffect(() => {
    if (usuarioLogueado && origenRegistro(usuarioLogueado) === 'Gov BR') {
      i18n.changeLanguage('pt');
    } else i18n.changeLanguage('es');
  }, [usuarioLogueado, i18n]);

  useEffect(() => {
    if (
      usuarioLogueado &&
      IDPS_EXTRANJEROS.includes(origenRegistro(usuarioLogueado))
    ) {
      setEsExtranjero(true);
      const { redirect_uri, state } = queryString.parse(
        history.location.search,
      );
      const { unadvanced_saml } = queryString.parse(history.location.search);
      if (redirect_uri && state)
        setRedirectParaExtranjero(`${redirect_uri}&state=${state}`);
      if (unadvanced_saml) setNivelBajoSaml(true);
      history.replace(history.location.pathname);
    }
  }, [usuarioLogueado]);

  const obtenerBandera = () => {
    const pais = usuarioLogueado?.documento.pais_emisor.nombre;
    if (pais === 'Argentina') return BANDERA_ARGENTINA;
    if (pais === 'Brasil') return BANDERA_BRASIL;
    if (pais === 'Chile') return BANDERA_CHILE;
    if (pais === 'Paraguay') return BANDERA_PARAGUAY;
    if (pais === 'Uruguay') return BANDERA_URUGUAY;
    return null;
  };

  const obtenerNombreBandera = () => {
    const pais = usuarioLogueado?.documento.pais_emisor.nombre;
    return `Bandera-${pais}`;
  };

  const camposDatosPrimerFila = () => ({
    [t('nombreYApellido')]: {
      texto: usuarioLogueado?.nombre_completo || t('sinDatos'),
      icono: <AccountBoxIcon />,
    },
    [t('nivel')]: {
      texto: nivelTexto || t('sinDatos'),
      icono: (
        <img
          className={styles.iconoNivelSeguridad}
          src={iconoNivelIntermedio}
          alt={INFO_ICONO_NIVEL_SEGURIDAD}
        />
      ),
    },
    [t('email')]: {
      texto: correoElectronico(usuarioLogueado) || t('sinDatos'),
      icono: <EmailIcon />,
    },
    [t('pais')]: {
      texto: usuarioLogueado?.documento.pais_emisor.nombre || t('sinDatos'),
      icono: <PublicIcon />,
    },
    [t('tipoDeDocumento')]: {
      texto: usuarioLogueado?.documento.tipo_documento.nombre || t('sinDatos'),
      icono: <BadgeIcon />,
    },
    [t('documento')]: {
      texto: usuarioLogueado?.numero_documento || t('sinDatos'),
      icono: <BadgeIcon />,
    },
  });

  if (cargandoUsuarioLogueado) {
    return <CardSpinner spin className="spinner__transparent" />;
  }

  return (
    <div className={styles.contenedorPrincipal}>
      <TitleCard
        className={styles.titulo}
        title={t('title', { idp: origenRegistro(usuarioLogueado) })}
      />
      {esExtranjero &&
        usuarioLogueado?.acr !== '3' &&
        (redirectParaExtranjero || nivelBajoSaml) && (
          <p className={styles.center}>{t('nivelBajoParaContinuarTramite')}</p>
        )}
      {esExtranjero && (redirectParaExtranjero || nivelBajoSaml) && (
        <ButtonSolid
          className={styles.center}
          text={t('continuarTramite')}
          handleClick={() => {
            if (redirectParaExtranjero)
              window.location.href = redirectParaExtranjero;
          }}
          isDisabled={usuarioLogueado?.acr !== '3' || nivelBajoSaml}
        />
      )}
      <Card innerClassName={styles.card}>
        <div className={styles.contenedorPrincipal}>
          <div className={styles.contenedorNombre}>
            <PersonIcon />
            {t('informacionPersonal')}
          </div>
          <div
            className={
              esMovil ? styles.contenedorDatosMovil : styles.contenedorDatos
            }
          >
            {Object.entries(camposDatosPrimerFila()).map(([key, value]) => (
              <div key={key} className={styles.contenedorDato}>
                <div className={styles.textoIcono}>
                  {value.icono}
                  <span className={styles.campo}>{key}</span>
                </div>
                <div className={styles.contenedorBanderaPais}>
                  {key === t('pais') &&
                    usuarioLogueado?.documento.pais_emisor.nombre && (
                      <img
                        className={styles.bandera}
                        src={obtenerBandera()}
                        alt={obtenerNombreBandera()}
                      ></img>
                    )}
                  <p
                    className={
                      key === t('pais') &&
                      usuarioLogueado?.documento.pais_emisor.nombre
                        ? styles.campoValorPais
                        : styles.campoValor
                    }
                  >
                    {value.texto}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {!esCiudadanoTemporal(usuarioLogueado) && (
        <div className={styles.linkSeguridad}>
          <Link to={ROUTES.SEGURIDAD_ROUTE}>{CARD_SEGURIDAD_TITLE}</Link>
        </div>
      )}
    </div>
  );
};

export default AutogestionIdp;
